/**
 * Pi.js - Images Module
 *
 * Image loading, storage, and management for WebGL2 renderer.
 *
 * @module api/images
 */

"use strict";

import * as g_alpha from "../renderer/alpha.js";
import * as g_utils from "../core/utils.js";
import * as g_errors from "../core/errors.js";
import * as g_commands from "../core/commands.js";
import * as g_screenManager from "../core/screen-manager.js";
import * as g_renderer from "../renderer/renderer.js";

// Image storage by name
const m_images = {};
let m_imageCount = 0;


/**************************************************************************************************
 * Module Initialization
 ************************************************************************************************/


/**
 * Initialize images module
 *
 * @param {Object} api - The main Pi.js API object
 * @returns {void}
 */
export function init( api ) {
	registerCommands( api );

	g_screenManager.addScreenDataItem( "defaultAnchorX", 0 );
	g_screenManager.addScreenDataItem( "defaultAnchorY", 0 );
}

/**
 * Register image commands
 *
 * @returns {void}
 */
function registerCommands( api ) {
	g_commands.addCommand(
		"loadImage", loadImage, false,
		[ "src", "name", "onLoad", "onError" ]
	);
	g_commands.addCommand(
		"loadSpritesheet", loadSpritesheet, false,
		[ "src", "name", "width", "height", "margin", "onLoad", "onError" ]
	);
	g_commands.addCommand( "getImage", getImage, false, [ "name" ] );
	g_commands.addCommand( "getSpritesheetData", getSpritesheetData, true, [ "name" ], true );
	g_commands.addCommand( "removeImage", removeImage, false, [ "name" ] );
	g_commands.addCommand(
		"createImageFromScreen", createImageFromScreen, true,
		[ "name", "x1", "y1", "x2", "y2" ]
	);
	g_commands.addCommand( "setDefaultAnchor", setDefaultAnchor, true, [ "x", "y" ] );
}


/**************************************************************************************************
 * External API Commands
 ************************************************************************************************/


/**
 * Load an image from URL or use provided Image/Canvas element
 *
 * @param {Object} options - Load options
 * @param {string|HTMLImageElement|HTMLCanvasElement|OffscreenCanvas} options.src - Image source
 * @param {string} [options.name] - Optional name for the image
 * @param {Function} [options.onLoad] - Callback when image loads
 * @param {Function} [options.onError] - Callback when image fails to load
 * @returns {string} Image name
 */
function loadImage( options ) {
	const src = options.src;
	let name = options.name;
	const onLoadCallback = options.onLoad;
	const onErrorCallback = options.onError;
	const srcErrMsg = "loadImage: Parameter src must be a string URL, Image element, or Canvas " +
		"element.";

	// Validate src parameter - can be string URL, Image element, or Canvas element
	if( typeof src === "string" ) {
		if( src === "" ) {
			g_errors.throwError( TypeError, srcErrMsg, "INVALID_SRC" );
		}
	} else if( src && typeof src === "object" ) {
		if( src.tagName !== "IMG" && src.tagName !== "CANVAS" ) {
			g_errors.throwError( TypeError, srcErrMsg, "INVALID_SRC" );
		}
	} else {
		g_errors.throwError( TypeError, srcErrMsg, "INVALID_SRC" );
	}

	// Validate name
	if( name && typeof name !== "string" ) {
		g_errors.throwError(
			TypeError, "loadImage: Parameter name must be a string.", "INVALID_NAME"
		);
	}

	// Generate a name if none is provided
	if( !name || name === "" ) {
		name = generateImageName();
	}
	if( m_images[ name ] ) {
		g_errors.throwError(
			TypeError, "loadImage: Parameter name must be unique.", "INVALID_NAME"
		);
	}

	// Validate callbacks if provided
	if( onLoadCallback != null && !g_utils.isFunction( onLoadCallback ) ) {
		g_errors.throwError(
			TypeError, "loadImage: Parameter onLoad must be a function.", "INVALID_CALLBACK"
		);
	}
	if( onErrorCallback != null && !g_utils.isFunction( onErrorCallback ) ) {
		g_errors.throwError(
			TypeError, "loadImage: Parameter onError must be a function.", "INVALID_CALLBACK"
		);
	}

	// Create blank image object
	const imageObj = {
		"status": "loading",
		"image": null,
		"width": null,
		"height": null
	};
	m_images[ name ] = imageObj;

	// Update function for when image is loaded to update the image object
	const updateImageFn = ( img ) => {

		// Use the element directly
		imageObj.image = img;
		imageObj.status = "ready";
		imageObj.width = img.width;
		imageObj.height = img.height;
	};

	// Handle Image or Canvas element passed directly
	if( typeof src !== "string" ) {

		// Use the element directly
		try {
			updateImageFn( src );
		} catch( error ) {
			removeImage( { "name": name } );
			throw error;
		}
		if( onLoadCallback ) {
			onLoadCallback( name );
		}
		return name;
	}

	// The load owns its element and one readiness wait, independently of the registered name.
	const load = {
		"image": null,
		"settled": false,
		"waiting": false,
		"releaseWait": () => {
			if( load.waiting ) {
				load.waiting = false;
				g_commands.done();
			}
		}
	};
	imageObj.load = load;

	try {
		const img = new Image();
		load.image = img;
		g_commands.wait();
		load.waiting = true;

		img.onload = function() {
			if( load.settled || m_images[ name ] !== imageObj ) {
				return;
			}
			finishImageLoad( load );
			try {
				updateImageFn( img );
				if( onLoadCallback ) {
					onLoadCallback( name );
				}
			} finally {
				load.releaseWait();
			}
		};

		img.onerror = function( error ) {
			if( load.settled || m_images[ name ] !== imageObj ) {
				return;
			}
			finishImageLoad( load );
			try {
				imageObj.status = "error";
				imageObj.error = error;
				if( onErrorCallback ) {
					onErrorCallback( error );
				}
			} finally {
				load.releaseWait();
			}
		};

		// A cached or controlled source may complete synchronously.
		img.src = src;
	} catch( error ) {

		// Completed callbacks may throw after removing or replacing their own registration.
		if( !load.settled ) {
			if( m_images[ name ] === imageObj ) {
				delete m_images[ name ];
			}
			cancelImageLoad( load );
		}
		throw error;
	}

	return name;
}


/**
 * Remove an image from storage
 * Pending URL loads cancel silently and release their readiness wait. Names are reusable at once.
 *
 * @param {Object} options - Load options
 * @param {string} [options.name] - Name of the image to remove
 * @returns {void}
 */
function removeImage( options ) {
	const name = options.name;
	if( typeof name !== "string" ) {
		g_errors.throwError(
			TypeError, "removeImage: Parameter name must be a string.", "INVALID_NAME"
		);
	}

	const imageObj = m_images[ name ];
	if( !imageObj ) {
		return;
	}

	// Release ownership before cancellation can generate any more events.
	delete m_images[ name ];
	if( imageObj.load && !imageObj.load.settled ) {
		cancelImageLoad( imageObj.load );
	}

	if( imageObj.image ) {

		const img = imageObj.image;

		// Explicitly delete WebGL2 textures to free GPU memory
		// WebGLTextures hold GPU memory that is NOT automatically freed by JS garbage collection
		// Must call gl.deleteTexture() explicitly to prevent memory leaks
		for( const screenData of g_screenManager.getAllScreensData() ) {
			g_renderer.deleteWebGL2Texture( screenData, img );
		}
	}
}

/**
 * Stop accepting terminal events and release the pending element reference.
 * @param {Object} load - Owned image load
 * @returns {void}
 */
function finishImageLoad( load ) {
	load.settled = true;
	if( load.image ) {
		load.image.onload = null;
		load.image.onerror = null;
		load.image = null;
	}
}

/**
 * Cancel an internally created image without invoking user callbacks.
 * @param {Object} load - Owned image load
 * @returns {void}
 */
function cancelImageLoad( load ) {
	const img = load.image;
	finishImageLoad( load );
	try {
		if( img ) {
			img.removeAttribute( "src" );
		}
	} finally {
		load.releaseWait();
	}
}

/**
 * Load a spritesheet from URL or use provided Image/Canvas element
 *
 * @param {Object} options - Load options
 * @param {string|HTMLImageElement|HTMLCanvasElement|OffscreenCanvas} options.src - Image source
 * @param {string} [options.name] - Optional name for the spritesheet
 * @param {number} [options.width] - Sprite width (required for fixed grid mode)
 * @param {number} [options.height] - Sprite height (required for fixed grid mode)
 * @param {number} [options.margin] - Margin between sprites, 0 or more (default: 0)
 * @param {Function} [options.onLoad] - Callback when spritesheet loads
 * @param {Function} [options.onError] - Callback when spritesheet fails to load
 * @returns {string} Spritesheet name
 */
function loadSpritesheet( options ) {
	const src = options.src;
	let name = options.name;
	let spriteWidth = options.width;
	let spriteHeight = options.height;
	let margin = options.margin;
	const onLoadCallback = options.onLoad;
	const onErrorCallback = options.onError;
	let isAuto = false;

	if( margin === null ) {
		margin = 0;
	}

	if( spriteWidth === null && spriteHeight === null ) {
		isAuto = true;
		spriteWidth = 0;
		spriteHeight = 0;
		margin = 0;
	} else {
		spriteWidth = Math.round( spriteWidth );
		spriteHeight = Math.round( spriteHeight );
		margin = Math.round( margin );
	}

	// Validate spriteWidth and spriteHeight for fixed mode
	if( !isAuto && ( !Number.isInteger( spriteWidth ) || !Number.isInteger( spriteHeight ) ) ) {
		g_errors.throwError(
			TypeError, "loadSpritesheet: width and height must be integers.", "INVALID_DIMENSIONS"
		);
	}

	// Size cannot be less than 1
	if( !isAuto && ( spriteWidth < 1 || spriteHeight < 1 ) ) {
		g_errors.throwError(
			RangeError, "loadSpritesheet: width and height must be greater than 0.",
			"INVALID_DIMENSIONS"
		);
	}

	// Validate margin; a negative margin would stop the grid from advancing
	if( !Number.isInteger( margin ) ) {
		g_errors.throwError(
			TypeError, "loadSpritesheet: margin must be an integer.", "INVALID_MARGIN"
		);
	}
	if( margin < 0 ) {
		g_errors.throwError(
			RangeError, "loadSpritesheet: margin must be 0 or more.", "INVALID_MARGIN"
		);
	}

	// Generate a name if none is provided
	if( !name || name === "" ) {
		name = generateImageName();
	}

	// Validate name
	if( typeof name !== "string" ) {
		g_errors.throwError(
			TypeError, "loadSpritesheet: Parameter name must be a string.", "INVALID_NAME"
		);
	}
	if( m_images[ name ] ) {
		g_errors.throwError(
			TypeError, "loadSpritesheet: Parameter name must be unique.", "INVALID_NAME"
		);
	}

	// Validate callbacks if provided
	if( onLoadCallback != null && !g_utils.isFunction( onLoadCallback ) ) {
		g_errors.throwError(
			TypeError, "loadSpritesheet: Parameter onLoad must be a function.", "INVALID_CALLBACK"
		);
	}
	if( onErrorCallback != null && !g_utils.isFunction( onErrorCallback ) ) {
		g_errors.throwError(
			TypeError, "loadSpritesheet: Parameter onError must be a function.", "INVALID_CALLBACK"
		);
	}

	// Load the image first, then process frames in callback
	loadImage( {
		"src": src,
		"name": name,
		"onLoad": function( imageName ) {

			// Update the image data to spritesheet type
			const imageData = m_images[ imageName ];
			imageData.type = "spritesheet";
			imageData.spriteWidth = spriteWidth;
			imageData.spriteHeight = spriteHeight;
			imageData.margin = margin;
			imageData.frames = [];
			imageData.isAuto = isAuto;

			const width = imageData.width;
			const height = imageData.height;

			if( isAuto ) {

				// Auto-detect sprites using connected component analysis
				processSpriteSheetAuto( imageData, width, height );
			} else {

				// Fixed grid of sprites
				processSpriteSheetFixed( imageData, width, height );
			}

			// Call user callback if provided
			if( onLoadCallback ) {
				onLoadCallback( imageName );
			}
		},
		"onError": onErrorCallback
	} );

	return name;
}

/**
 * Gets the image by name and returns the DOM element (Image or Canvas). A screen gives its
 * canvas; an offscreen screen, which has no canvas of its own, gives a new canvas holding a copy
 * of its pixels.
 *
 * @param {Object} options - Options
 * @param {string|Object} options.name - Image name, or a screen
 * @returns {Object} Actual image
 */
function getImage( options ) {
	const img = getImageFromRawInput( options.name, "getImage" );
	if( img.isMock ) {
		const imgScreenData = g_screenManager.getScreenData( "getImage", img.dataset.screenId );
		return createCanvasFromScreenRegion(
			imgScreenData, 0, 0, imgScreenData.width, imgScreenData.height
		);
	}
	return img;
}

/**
 * Create an image from a region of the screen (FBO)
 * Defaults to fullscreen if no coordinates are provided
 *
 * @param {Object} screenData - Screen data object
 * @param {Object} options - Options
 * @param {string} [options.name] - Optional name for the image
 * @param {number} [options.x1] - Left coordinate (defaults to 0)
 * @param {number} [options.y1] - Top coordinate (defaults to 0)
 * @param {number} [options.x2] - Right coordinate (defaults to screen width)
 * @param {number} [options.y2] - Bottom coordinate (defaults to screen height)
 * @returns {string} Image name
 */
function createImageFromScreen( screenData, options ) {
	let name = options.name;
	const view = screenData.view;
	let x1 = g_utils.getInt( options.x1, 0 );
	let y1 = g_utils.getInt( options.y1, 0 );
	let x2 = g_utils.getInt( options.x2, view.width - 1 );
	let y2 = g_utils.getInt( options.y2, view.height - 1 );

	if( view.clipWidth <= 0 || view.clipHeight <= 0 ) {
		g_errors.throwError(
			RangeError, "createImageFromScreen: Region width and height must be greater than 0.",
			"INVALID_DIMENSIONS"
		);
	}

	// Inclusive local clip limits from the effective physical clip
	const minLocalX = view.clipX - view.originX;
	const minLocalY = view.clipY - view.originY;
	const maxLocalX = view.clipX + view.clipWidth - 1 - view.originX;
	const maxLocalY = view.clipY + view.clipHeight - 1 - view.originY;

	x1 = g_utils.clamp( x1, minLocalX, maxLocalX );
	y1 = g_utils.clamp( y1, minLocalY, maxLocalY );
	x2 = g_utils.clamp( x2, minLocalX, maxLocalX );
	y2 = g_utils.clamp( y2, minLocalY, maxLocalY );

	const width = Math.abs( x2 - x1 ) + 1;
	const height = Math.abs( y2 - y1 ) + 1;

	if( width === 0 || height === 0 ) {
		g_errors.throwError(
			RangeError, "createImageFromScreen: Region width and height must be greater than 0.",
			"INVALID_DIMENSIONS"
		);
	}

	// Physical top-left of the clamped inclusive corners
	const actualX = Math.min( x1, x2 ) + view.originX;
	const actualY = Math.min( y1, y2 ) + view.originY;

	// Generate a name if none is provided
	if( !name || name === "" ) {
		name = generateImageName();
	} else if( typeof name !== "string" ) {
		g_errors.throwError(
			TypeError, "createImageFromScreen: Parameter name must be a string.", "INVALID_NAME"
		);
	} else if( m_images[ name ] ) {
		g_errors.throwError(
			Error, `createImageFromScreen: name "${name}" is already used; name must be unique.`,
			"DUPLICATE_NAME"
		);
	}

	// Create canvas copy using helper function
	const canvas = createCanvasFromScreenRegion( screenData, actualX, actualY, width, height );

	// Store in m_images
	m_images[ name ] = {
		"status": "ready",
		"image": canvas,
		"width": width,
		"height": height
	};

	return name;
}

/**
 * Set the default anchor point for images on this screen
 *
 * @param {Object} screenData - Screen data object
 * @param {Object} options - Options
 * @param {number} options.anchorX - Anchor point X (0-1)
 * @param {number} options.anchorY - Anchor point Y (0-1)
 * @returns {void}
 */
function setDefaultAnchor( screenData, options ) {
	const anchorX = g_utils.getFloat( options.x, null );
	const anchorY = g_utils.getFloat( options.y, null );

	// Validate anchorX
	if( anchorX === null || anchorX < 0 || anchorX > 1 ) {
		g_errors.throwError(
			TypeError, "setDefaultAnchor: Parameter x must be a number between 0 and 1.",
			"INVALID_ANCHOR"
		);
	}

	// Validate anchorY
	if( anchorY === null || anchorY < 0 || anchorY > 1 ) {
		g_errors.throwError(
			TypeError, "setDefaultAnchor: Parameter y must be a number between 0 and 1.",
			"INVALID_ANCHOR"
		);
	}

	// Update default anchor values
	screenData.defaultAnchorX = anchorX;
	screenData.defaultAnchorY = anchorY;
}

/**
 * Get spritesheet data including frame information
 *
 * @param {Object} screenData - Screen data object
 * @param {Object} options - Options
 * @param {string} options.name - Spritesheet name
 * @returns {Object} Spritesheet data with frameCount and frames array
 */
function getSpritesheetData( screenData, options ) {
	const name = options.name;

	// Validate name
	if( typeof name !== "string" ) {
		g_errors.throwError(
			TypeError, "getSpritesheetData: Parameter name must be a string.", "INVALID_NAME"
		);
	}

	const spriteData = getStoredImage( name );
	if( !spriteData ) {
		g_errors.throwError(
			Error, `getSpritesheetData: Spritesheet "${name}" not found.`, "IMAGE_NOT_FOUND"
		);
	}

	if( spriteData.type !== "spritesheet" ) {
		g_errors.throwError(
			Error, `getSpritesheetData: Image "${name}" is not a spritesheet.`, "NOT_A_SPRITESHEET"
		);
	}

	const spriteDataResult = {
		"frameCount": spriteData.frames.length,
		"frames": []
	};

	for( let i = 0; i < spriteData.frames.length; i++ ) {
		spriteDataResult.frames.push( {
			"index": i,
			"x": spriteData.frames[ i ].x,
			"y": spriteData.frames[ i ].y,
			"width": spriteData.frames[ i ].width,
			"height": spriteData.frames[ i ].height,
			"left": spriteData.frames[ i ].x,
			"top": spriteData.frames[ i ].y,
			"right": spriteData.frames[ i ].right,
			"bottom": spriteData.frames[ i ].bottom
		} );
	}

	return spriteDataResult;
}


/**************************************************************************************************
 * Internal Helper Functions
 ************************************************************************************************/


/**
 * Create a canvas copy from a region of the screen (FBO)
 *
 * @param {Object} screenData - Screen data object
 * @param {number} x - X coordinate of region
 * @param {number} y - Y coordinate of region
 * @param {number} width - Width of region
 * @param {number} height - Height of region
 * @returns {HTMLCanvasElement} Canvas element with copy of screen region
 */
function createCanvasFromScreenRegion( screenData, x, y, width, height ) {

	// TODO-LATER: Research if it's possible to use blitFrameBuffer or another faster method than
	// readPixels.

	// Read pixel data from FBO using readPixelsRaw
	const pixelData = g_renderer.readPixelsRaw( screenData, x, y, width, height );

	if( !pixelData ) {
		g_errors.throwError(
			Error, "createCanvasFromScreenRegion: Failed to read pixel data from screen.",
			"READ_FAILED"
		);
	}

	// Create canvas
	const canvas = document.createElement( "canvas" );
	canvas.width = width;
	canvas.height = height;
	const context = canvas.getContext( "2d" );

	// Create ImageData for canvas (top-left origin)
	g_alpha.unpremultiplyPixels( pixelData );

	const imageData = context.createImageData( width, height );
	const canvasData = imageData.data;

	// Convert from bottom-left origin (WebGL) to top-left origin (Canvas)
	// pixelData is ordered from bottom row to top row
	// canvasData needs to be ordered from top row to bottom row
	for( let row = 0; row < height; row++ ) {
		for( let col = 0; col < width; col++ ) {

			// Source row in bottom-left origin (pixelData)
			const srcRow = ( height - 1 ) - row;
			const srcIndex = ( srcRow * width + col ) * 4;

			// Destination row in top-left origin (canvasData)
			const dstIndex = ( row * width + col ) * 4;

			// Copy RGBA values
			canvasData[ dstIndex     ] = pixelData[ srcIndex     ];
			canvasData[ dstIndex + 1 ] = pixelData[ srcIndex + 1 ];
			canvasData[ dstIndex + 2 ] = pixelData[ srcIndex + 2 ];
			canvasData[ dstIndex + 3 ] = pixelData[ srcIndex + 3 ];
		}
	}

	// Put image data into canvas
	context.putImageData( imageData, 0, 0 );

	return canvas;
}

/**
 * Resolve a ready named image, screen, or browser image source for drawing.
 *
 * @param {string|Object} imageOrName - Image name, screen API, or browser image source.
 * @param {string} fnName - Command name used in errors.
 * @returns {Object}
 */
export function getImageFromRawInput( imageOrName, fnName ) {
	let img = null;

	// Resolve image from name parameter
	if( typeof imageOrName === "string" ) {

		// Handle string image name
		const imageData = getStoredImage( imageOrName );
		if( !imageData ) {
			g_errors.throwError(
				Error, `${fnName}: Image "${imageOrName}" not found.`, "IMAGE_NOT_FOUND"
			);
		}

		// Make sure image is ready
		if( imageData.status !== "ready" ) {
			const imgName = `Image "${imageOrName}"`;
			if( imageData.status === "loading" ) {
				g_errors.throwError(
					Error,
					`${fnName}: "${imgName}" is still loading. Use $.ready() to wait for it.`,
					"IMAGE_NOT_READY"
				);
			}
			if( imageData.status === "error" ) {
				g_errors.throwError(
					Error, `${fnName}: "${imgName}" failed to load.`, "IMAGE_LOAD_FAILED"
				);
			}
		}

		// Set the image
		img = imageData.image;
	} else if( imageOrName && typeof imageOrName === "object" ) {
		if( isTexImageCompatible( imageOrName ) ) {
			img = imageOrName;
		} else if( imageOrName.screen === true ) {
			const imgScreenData = g_screenManager.getScreenData( fnName, imageOrName.id );
			img = imgScreenData.canvas;
		}
	}

	if( img === null ) {
		g_errors.throwError(
			TypeError,
			`${fnName}: Parameter name must be a string, canvas element, or image element.`,
			"INVALID_NAME"
		);
	}

	return img;
}

function isTexImageCompatible( img ) {
	return (
		img instanceof HTMLImageElement ||
		img instanceof HTMLVideoElement ||
		img instanceof HTMLCanvasElement ||
		img instanceof ImageBitmap ||
		img instanceof ImageData ||
		( typeof OffscreenCanvas !== "undefined" && img instanceof OffscreenCanvas )
	);
}

/**
 * Generate a name for an image registered without one. Explicit names can be numeric, so the
 * counter skips any name already registered, whether ready, loading, or failed.
 *
 * @returns {string} Unused image name
 */
function generateImageName() {
	let name;
	do {
		m_imageCount += 1;
		name = "" + m_imageCount;
	} while( m_images[ name ] );
	return name;
}

/**
 * Get stored image by name
 *
 * @param {string} name - Image name
 * @returns {Object|null} Image data object or null if not found
 */
export function getStoredImage( name ) {
	if( typeof name !== "string" ) {
		return null;
	}
	return m_images[ name ] || null;
}

/**
 * Process spritesheet with fixed grid dimensions
 *
 * @param {Object} imageData - Image data object
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {void}
 */
function processSpriteSheetFixed( imageData, width, height ) {
	let x1 = imageData.margin;
	let y1 = imageData.margin;
	let x2 = x1 + imageData.spriteWidth;
	let y2 = y1 + imageData.spriteHeight;

	// Loop through all the frames in a grid pattern
	while( y2 <= height - imageData.margin ) {
		while( x2 <= width - imageData.margin ) {
			imageData.frames.push( {
				"x": x1,
				"y": y1,
				"width": imageData.spriteWidth,
				"height": imageData.spriteHeight,
				"right": x1 + imageData.spriteWidth - 1,
				"bottom": y1 + imageData.spriteHeight - 1
			} );
			x1 += imageData.spriteWidth + imageData.margin;
			x2 = x1 + imageData.spriteWidth;
		}
		x1 = imageData.margin;
		x2 = x1 + imageData.spriteWidth;
		y1 += imageData.spriteHeight + imageData.margin;
		y2 = y1 + imageData.spriteHeight;
	}
}

/**
 * Process spritesheet with auto-detection (finds connected pixel clusters)
 *
 * @param {Object} imageData - Image data object
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {void}
 */
function processSpriteSheetAuto( imageData, width, height ) {

	// Create canvas to read pixel data
	const canvas = document.createElement( "canvas" );
	canvas.width = width;
	canvas.height = height;
	const context = canvas.getContext( "2d", { "willReadFrequently": true } );
	context.drawImage( imageData.image, 0, 0 );

	const data = context.getImageData( 0, 0, width, height ).data;
	const searched = new Uint8Array( width * height );

	// 8-directional neighbors
	const dirs = [
		[ -1, -1 ], [ 0, -1 ], [ 1, -1 ],
		[ -1,  0 ],            [ 1,  0 ],
		[ -1,  1 ], [ 0,  1 ], [ 1,  1 ]
	];

	// Find clusters of non-transparent pixels
	for( let i = 3; i < data.length; i += 4 ) {
		if( data[ i ] > 0 ) {
			const index = ( i - 3 ) / 4;
			const x1 = index % width;
			const y1 = Math.floor( index / width );
			const pixelIndex = y1 * width + x1;

			// Skip if already searched
			if( searched[ pixelIndex ] ) {
				continue;
			}

			// Find bounding box of this cluster
			const frameData = {
				"x": width,
				"y": height,
				"width": 0,
				"height": 0,
				"right": 0,
				"bottom": 0
			};

			// BFS to find all connected pixels
			const queue = [];
			queue.push( { "x": x1, "y": y1 } );
			searched[ pixelIndex ] = 1;

			let head = 0;
			while( head < queue.length ) {
				const pixel = queue[ head++ ];
				const px = pixel.x;
				const py = pixel.y;

				// Update bounding box
				frameData.x = Math.min( frameData.x, px );
				frameData.y = Math.min( frameData.y, py );
				frameData.right = Math.max( frameData.right, px );
				frameData.bottom = Math.max( frameData.bottom, py );

				// Check all 8 neighbors
				for( const dir of dirs ) {
					const nx = px + dir[ 0 ];
					const ny = py + dir[ 1 ];

					// Skip if out of bounds
					if( nx < 0 || nx >= width || ny < 0 || ny >= height ) {
						continue;
					}

					const nIndex = ny * width + nx;

					// Skip if already searched
					if( searched[ nIndex ] ) {
						continue;
					}

					// Check if pixel is non-transparent
					const dataIndex = nIndex * 4;
					if( data[ dataIndex + 3 ] > 0 ) {
						searched[ nIndex ] = 1;
						queue.push( { "x": nx, "y": ny } );
					}
				}
			}

			// Calculate final dimensions
			frameData.width = frameData.right - frameData.x + 1;
			frameData.height = frameData.bottom - frameData.y + 1;

			// Only add frames larger than 4 pixels (filters out noise)
			if( ( frameData.width + frameData.height ) > 4 ) {
				imageData.frames.push( frameData );
			}
		}
	}
}
